import { motion } from 'framer-motion'
import type { ReactNode } from 'react'

import { tabPanelVariants } from '@/lib/motion'

export interface TabPanelProps {
  /** The active tab's key. Changing it replays the entrance. */
  tabKey: string
  children: ReactNode
  className?: string
}

/* Base UI (`@base-ui/react`): `Tabs.Panel` considered and rejected. It requires a `Tabs.Root`
   ancestor that owns the selection, and this panel is driven by the router
   alongside TabNav, which is not a tablist. */

/* Wraps whichever panel a tab bar is currently showing.
 *
 * The `key` is what does the work: React tears down the old subtree and builds
 * the new one whenever it changes, so `initial` runs again on every switch.
 * Without it the panel would animate once on mount and then swap contents
 * silently for the rest of the session.
 *
 * There is no AnimatePresence and no exit state here, deliberately. Holding the
 * outgoing panel on screen to animate it away would delay the incoming one by
 * its full duration, and these panels carry the balances and approvals people
 * came to read. A section switch should feel like the data was already there.
 */
export function TabPanel({ tabKey, children, className }: TabPanelProps) {
  return (
    <motion.div
      key={tabKey}
      variants={tabPanelVariants}
      initial="hidden"
      animate="visible"
      className={className}
    >
      {children}
    </motion.div>
  )
}
